import { create } from 'zustand';
import type { AdminAccessResponse } from '@/core/generated/api';
import { RequestStatus } from './requestStatus';

interface AdminAccessData {
    snapshot: AdminAccessResponse | null;
    status: RequestStatus;
    error: string | null;
    revision: number;
}

interface AdminAccessActions {
    /** Регистрирует проверку прав сотрудника */
    begin: () => number;

    /** Применяет актуальные возможности сотрудника */
    complete: (revision: number, snapshot: AdminAccessResponse) => void;

    /** Закрывает доступ при невозможности проверить права */
    fail: (revision: number, error: string) => void;

    /** Закрывает доступ при завершении или смене сессии */
    clear: () => void;
}

/** Хранит возможности текущей административной сессии */
export const useAdminAccess = create<AdminAccessData & AdminAccessActions>((set, get) => ({
    snapshot: null,
    status: RequestStatus.Idle,
    error: null,
    revision: 0,

    begin: () => {
        const revision = get().revision + 1;

        set({
            revision,
            status: get().snapshot
                ? RequestStatus.Success
                : RequestStatus.Loading,
            error: null,
        });

        return revision;
    },

    complete: (revision, snapshot) => {
        if (revision !== get().revision) return;

        set({
            snapshot,
            status: RequestStatus.Success,
            error: null
        });
    },

    fail: (revision, error) => {
        if (revision !== get().revision) return;

        set({
            snapshot: null,
            status: RequestStatus.Error,
            error
        });
    },

    clear: () => set({
        snapshot: null,
        status: RequestStatus.Idle,
        error: null,
        revision: get().revision + 1,
    }),
}));

/** Проверяет доступ к известному административному праву */
export const canAccessPermission = (
    snapshot: AdminAccessResponse | null,
    code: string
): boolean => (
    snapshot?.isStaff === true
    && (snapshot.hasFullAccess === true || (snapshot.permissions ?? []).some((permission) => (
        permission.code === code && permission.isAllowed === true
    )))
);

/** Проверяет доступ к зарегистрированному шаблону маршрута админки */
export const canAccessRoute = (snapshot: AdminAccessResponse | null, route: string): boolean => {
    if (snapshot?.isStaff !== true) return false;
    if (snapshot.hasFullAccess === true) return true;

    const pathname = route.split(/[?#]/, 1)[0];

    return (snapshot.permissions ?? []).some((permission) => (
        permission.isAllowed === true && (permission.uiRoutes ?? []).includes(pathname)
    ));
};

/** Возвращает доступ к прикладному действию */
export const useCanAccessPermission = (code: string): boolean => (
    useAdminAccess((state) => canAccessPermission(state.snapshot, code))
);
